/**
 * EntrySelectionStep - Step 1: Choose entry point
 */

import React from 'react';
import { Card } from '@/components/ui/card';
import { PenLine, Search, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EntryPoint } from '../ContentPlannerWizard';

interface EntrySelectionStepProps {
  selectedPath: EntryPoint | null;
  onSelectPath: (path: EntryPoint) => void;
}

const EntrySelectionStep: React.FC<EntrySelectionStepProps> = ({
  selectedPath,
  onSelectPath,
}) => {
  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold mb-2">Choose how you want to create content</h2>
        <p className="text-muted-foreground">
          Select the approach that best fits your workflow
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Option A: I have keywords */}
        <Card
          className={cn(
            'p-6 cursor-pointer hover:border-primary hover:shadow-md transition-all group',
            selectedPath === 'have-keywords' && 'border-primary bg-primary/5 shadow-md'
          )}
          onClick={() => onSelectPath('have-keywords')}
        >
          <div className="flex flex-col items-center text-center space-y-4">
            <div className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center transition-colors',
              selectedPath === 'have-keywords'
                ? 'bg-green-200'
                : 'bg-green-100 dark:bg-green-900/40 group-hover:bg-green-200'
            )}>
              <PenLine className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-1">I have keywords</h3>
              <p className="text-sm text-muted-foreground">
                Enter your target keywords and create blog posts directly
              </p>
            </div>
            {selectedPath === 'have-keywords' && (
              <div className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 font-medium">
                <span>Selected</span>
                <ChevronRight className="h-4 w-4" />
              </div>
            )}
          </div>
        </Card>

        {/* Option B: Find keywords */}
        <Card
          className={cn(
            'p-6 cursor-pointer hover:border-primary hover:shadow-md transition-all group',
            selectedPath === 'find-keywords' && 'border-primary bg-primary/5 shadow-md'
          )}
          onClick={() => onSelectPath('find-keywords')}
        >
          <div className="flex flex-col items-center text-center space-y-4">
            <div className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center transition-colors',
              selectedPath === 'find-keywords'
                ? 'bg-blue-200'
                : 'bg-blue-100 dark:bg-blue-900/40 group-hover:bg-blue-200'
            )}>
              <Search className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-1">Find keywords</h3>
              <p className="text-sm text-muted-foreground">
                Discover related keywords, view clusters, then create content
              </p>
            </div>
            {selectedPath === 'find-keywords' && (
              <div className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 font-medium">
                <span>Selected</span>
                <ChevronRight className="h-4 w-4" />
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EntrySelectionStep;
